import { AxiosError } from 'axios';
import type { ThemeColors } from '../context/ThemeContext';
import {
  CatalogItem,
  DocumentCategory,
  DocumentRequestStatus,
  DocumentStatusTone,
  FormField,
  ParentDocument,
  ParentRequestSummary,
  ParentRequestView,
  PickedDocumentFile,
  TimelineState,
} from '../types';
import { parseBackendDate } from './dateUtils';

// Pure logic for the student documents feature, shared by the native and web
// screens. The server re-checks everything here; these checks only exist so a
// parent hears about a problem before waiting on a round trip, so every rule
// mirrors DocumentFormValidator.cs rather than inventing a stricter one.

// ── Status presentation ─────────────────────────────────────────────────────

export interface ToneColors {
  fg: string;
  bg: string;
}

/** The server's statusTone mapped onto the app's theme tokens. */
export function documentToneColors(colors: ThemeColors, tone: DocumentStatusTone | string): ToneColors {
  switch (tone) {
    case 'success':
      return { fg: colors.success, bg: colors.successLight };
    case 'warning':
      return { fg: colors.warning, bg: colors.warningLight };
    case 'danger':
      return { fg: colors.danger, bg: colors.dangerLight };
    case 'info':
      return { fg: colors.primary, bg: colors.primaryLight };
    case 'neutral':
    default:
      return { fg: colors.textSecondary, bg: colors.divider };
  }
}

// Colour is never the only signal: every status also carries an icon.
const STATUS_ICONS: Record<DocumentRequestStatus, string> = {
  SUBMITTED: 'send',
  UNDER_REVIEW: 'search',
  CORRECTION_REQUIRED: 'alert-triangle',
  RESUBMITTED: 'search',
  APPROVED: 'check-circle',
  REJECTED: 'x-circle',
  GENERATION_PENDING: 'loader',
  GENERATED: 'download',
  DELIVERED: 'check-circle',
  CANCELLED: 'slash',
};

export function requestStatusIcon(status: DocumentRequestStatus | string): string {
  return STATUS_ICONS[status as DocumentRequestStatus] ?? 'circle';
}

/** Tone and icon for a document in the locker. Revoked and expired copies cannot be downloaded. */
export function lockerStatusMeta(status: ParentDocument['status'] | string): {
  tone: DocumentStatusTone;
  icon: string;
} {
  switch (status) {
    case 'VALID':
      return { tone: 'success', icon: 'check-circle' };
    case 'REVOKED':
      return { tone: 'danger', icon: 'x-octagon' };
    case 'EXPIRED':
    default:
      return { tone: 'neutral', icon: 'clock' };
  }
}

export function timelineTone(state: TimelineState | string): DocumentStatusTone {
  switch (state) {
    case 'DONE':
      return 'success';
    case 'CURRENT':
      return 'info';
    case 'FAILED':
      return 'danger';
    case 'PENDING':
    default:
      return 'neutral';
  }
}

export type RequestPrimaryAction = 'download' | 'update' | 'view';

/** The one thing a parent most likely wants to do with a request right now. */
export function primaryRequestAction(
  request: Pick<ParentRequestSummary, 'canDownload' | 'canResubmit' | 'generatedDocumentId'>,
): RequestPrimaryAction {
  if (request.canDownload && request.generatedDocumentId) return 'download';
  if (request.canResubmit) return 'update';
  return 'view';
}

export const REQUEST_VIEWS: { value: ParentRequestView; label: string; countKey: string }[] = [
  { value: 'OPEN', label: 'Open', countKey: 'OPEN' },
  { value: 'ACTION_REQUIRED', label: 'Action required', countKey: 'ACTION_REQUIRED' },
  { value: 'COMPLETED', label: 'Completed', countKey: 'COMPLETED' },
  { value: '', label: 'All', countKey: 'ALL' },
];

/** Counts arrive keyed by view name; read them case-insensitively and default to 0. */
export function viewCount(counts: Record<string, number> | null | undefined, countKey: string): number {
  if (!counts) return 0;
  const match = Object.keys(counts).find(key => key.toUpperCase() === countKey.toUpperCase());
  return match ? counts[match] ?? 0 : 0;
}

export function emptyRequestsMessage(view: ParentRequestView): string {
  switch (view) {
    case 'OPEN':
      return 'No open requests';
    case 'ACTION_REQUIRED':
      return 'Nothing needs your attention';
    case 'COMPLETED':
      return 'No completed requests yet';
    case 'CLOSED':
      return 'No closed requests';
    default:
      return 'No document requests yet';
  }
}

// ── Catalogue ───────────────────────────────────────────────────────────────

export function catalogCategories(items: CatalogItem[]): { category: DocumentCategory; label: string }[] {
  const seen = new Map<DocumentCategory, string>();
  items.forEach(item => {
    if (!seen.has(item.category)) seen.set(item.category, item.categoryLabel || item.category);
  });
  return Array.from(seen.entries()).map(([category, label]) => ({ category, label }));
}

export function filterCatalog(
  items: CatalogItem[],
  search: string,
  category: DocumentCategory | 'ALL',
): CatalogItem[] {
  const needle = search.trim().toLowerCase();
  return items.filter(item => {
    if (category !== 'ALL' && item.category !== category) return false;
    if (!needle) return true;
    return [item.name, item.code, item.description, item.categoryLabel]
      .filter(Boolean)
      .some(text => String(text).toLowerCase().includes(needle));
  });
}

// ── Dynamic form ────────────────────────────────────────────────────────────

export type FormValues = Record<string, string>;
export type FormFiles = Record<string, PickedDocumentFile>;

const READ_ONLY_FIELD_TYPES = ['STUDENT_FIELD', 'PARENT_FIELD'];

export function isReadOnlyField(field: Pick<FormField, 'fieldType'>): boolean {
  return READ_ONLY_FIELD_TYPES.includes(field.fieldType);
}

/** Same spellings the server accepts as a ticked checkbox. */
export function isTruthy(value?: string | null): boolean {
  if (value == null) return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

/** Whether a field is shown, given the other answers. Hidden fields are neither required nor sent. */
export function isFieldVisible(field: Pick<FormField, 'condition'>, values: FormValues): boolean {
  const condition = field.condition;
  if (!condition || !condition.fieldKey || !condition.fieldKey.trim()) return true;

  const actualKey = Object.keys(values).find(
    key => key.toLowerCase() === condition.fieldKey.toLowerCase(),
  );
  const actual = ((actualKey ? values[actualKey] : '') ?? '').trim().toLowerCase();
  const expected = (condition.value ?? '').trim().toLowerCase();

  switch (condition.operator) {
    case 'NOT_EQUALS':
      return actual !== expected;
    case 'IN':
      return (condition.value ?? '')
        .split(',')
        .map(part => part.trim().toLowerCase())
        .filter(Boolean)
        .includes(actual);
    case 'EQUALS':
    default:
      return actual === expected;
  }
}

/** The starting answers: blank for a new request, the earlier answers for a resubmission. */
export function initialFormValues(fields: FormField[]): FormValues {
  const values: FormValues = {};
  fields.forEach(field => {
    if (isReadOnlyField(field) || field.fieldType === 'FILE') return;
    const current = field.currentValue ?? '';
    values[field.key] = field.fieldType === 'CHECKBOX' ? (isTruthy(current) ? 'true' : '') : current;
  });
  return values;
}

const DEFAULT_FILE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const DEFAULT_FILE_MAX_MB = 5;
const ABSOLUTE_FILE_MAX_MB = 10;

const DEFAULT_TEXT_MAX = 500;
const DEFAULT_TEXTAREA_MAX = 2000;

export function fileRule(field: Pick<FormField, 'validation'>): { extensions: string[]; maxMb: number } {
  const configured = (field.validation?.allowedExtensions ?? [])
    .map(ext => ext.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);
  const requested = field.validation?.maxSizeMb;
  const maxMb =
    typeof requested === 'number' && requested > 0
      ? Math.min(requested, ABSOLUTE_FILE_MAX_MB)
      : DEFAULT_FILE_MAX_MB;
  return { extensions: configured.length > 0 ? configured : DEFAULT_FILE_EXTENSIONS, maxMb };
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Checks a chosen file against the field's extension and size rule. Null when it is fine. */
export function validatePickedFile(
  field: Pick<FormField, 'validation'>,
  file: Pick<PickedDocumentFile, 'name' | 'size'>,
): string | null {
  const rule = fileRule(field);
  const ext = fileExtension(file.name);
  if (!rule.extensions.includes(ext)) {
    return `Choose a ${rule.extensions.map(e => e.toUpperCase()).join(', ')} file.`;
  }
  if (typeof file.size === 'number' && file.size > rule.maxMb * 1024 * 1024) {
    return `The file must be ${rule.maxMb} MB or smaller.`;
  }
  return null;
}

/** One answer against its field. Null when it is fine; otherwise a sentence to show under the field. */
export function validateFieldValue(field: FormField, rawValue: string | undefined): string | null {
  const value = (rawValue ?? '').trim();
  const empty = value.length === 0;

  // A required checkbox is a declaration, so it must be ticked rather than merely answered.
  if (field.fieldType === 'CHECKBOX') {
    return field.isRequired && !isTruthy(value) ? 'Please tick to confirm.' : null;
  }

  if (empty) return field.isRequired ? 'This is required.' : null;

  const validation = field.validation;
  switch (field.fieldType) {
    case 'TEXT':
    case 'TEXTAREA': {
      const max =
        validation?.maxLength ?? (field.fieldType === 'TEXTAREA' ? DEFAULT_TEXTAREA_MAX : DEFAULT_TEXT_MAX);
      if (value.length > max) return `Keep it to ${max} characters.`;
      if (typeof validation?.minLength === 'number' && value.length < validation.minLength) {
        return `Enter at least ${validation.minLength} characters.`;
      }
      return null;
    }
    case 'DROPDOWN':
    case 'RADIO':
      return (field.options ?? []).some(option => option.toLowerCase() === value.toLowerCase())
        ? null
        : 'Choose one of the listed options.';
    case 'NUMBER': {
      const normalised = value.replace(/,/g, '');
      if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(normalised)) return 'Enter a number.';
      const number = Number(normalised);
      if (typeof validation?.min === 'number' && number < validation.min) {
        return `Must be at least ${validation.min}.`;
      }
      if (typeof validation?.max === 'number' && number > validation.max) {
        return `Must be at most ${validation.max}.`;
      }
      return null;
    }
    case 'DATE':
      return isValidIsoDate(value) ? null : 'Enter a valid date.';
    default:
      return null;
  }
}

export function isValidIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

export interface ValidateFormOptions {
  /** Resubmission: file fields that already hold an upload, so "required" is satisfied. */
  existingFileKeys?: string[];
}

/**
 * Every visible, editable field checked. Read-only fields, hidden fields and fields the
 * school did not reopen on a correction are skipped, exactly as the server skips them.
 */
export function validateDocumentForm(
  fields: FormField[],
  values: FormValues,
  files: FormFiles,
  options: ValidateFormOptions = {},
): Record<string, string> {
  const errors: Record<string, string> = {};
  const existing = new Set((options.existingFileKeys ?? []).map(key => key.toLowerCase()));

  fields.forEach(field => {
    if (isReadOnlyField(field) || field.isEditable === false) return;
    if (!isFieldVisible(field, values)) return;

    if (field.fieldType === 'FILE') {
      const picked = files[field.key];
      if (picked) {
        const problem = validatePickedFile(field, picked);
        if (problem) errors[field.key] = problem;
      } else if (field.isRequired && !existing.has(field.key.toLowerCase())) {
        errors[field.key] = 'Please attach a file.';
      }
      return;
    }

    const problem = validateFieldValue(field, values[field.key]);
    if (problem) errors[field.key] = problem;
  });

  return errors;
}

/**
 * The answers to send: hidden and read-only fields dropped, checkboxes as "true"/"false",
 * text trimmed. On a resubmission only the fields the school reopened are sent.
 */
export function buildSubmissionValues(fields: FormField[], values: FormValues): FormValues {
  const out: FormValues = {};
  fields.forEach(field => {
    if (isReadOnlyField(field) || field.fieldType === 'FILE' || field.isEditable === false) return;
    if (!isFieldVisible(field, values)) return;
    const raw = values[field.key] ?? '';
    if (field.fieldType === 'CHECKBOX') {
      out[field.key] = isTruthy(raw) ? 'true' : 'false';
      return;
    }
    const trimmed = raw.trim();
    if (trimmed) out[field.key] = trimmed;
  });
  return out;
}

/** Only files for fields that are still visible and editable travel with the request. */
export function buildSubmissionFiles(fields: FormField[], values: FormValues, files: FormFiles): FormFiles {
  const out: FormFiles = {};
  fields.forEach(field => {
    if (field.fieldType !== 'FILE' || field.isEditable === false) return;
    if (!isFieldVisible(field, values)) return;
    const picked = files[field.key];
    if (picked) out[field.key] = picked;
  });
  return out;
}

/** How an answer reads on the review step and in request detail. */
export function displayAnswer(
  field: Pick<FormField, 'fieldType' | 'readOnlyValue'>,
  value: string | null | undefined,
  file?: Pick<PickedDocumentFile, 'name'> | null,
): string {
  if (field.fieldType === 'STUDENT_FIELD' || field.fieldType === 'PARENT_FIELD') {
    return field.readOnlyValue?.trim() || value?.trim() || '—';
  }
  if (field.fieldType === 'FILE') return file?.name ?? (value?.trim() || 'No file attached');
  if (field.fieldType === 'CHECKBOX') return isTruthy(value) ? 'Yes' : 'No';
  if (field.fieldType === 'DATE' && value && isValidIsoDate(value.trim())) return formatDocDate(value);
  return value?.trim() || '—';
}

// ── Formatting ──────────────────────────────────────────────────────────────

export function formatFee(fee: number | null | undefined): string {
  if (!fee || fee <= 0) return 'Free';
  const [whole, cents] = fee.toFixed(2).split('.');
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents}`;
}

/** True when a turnaround is configured (> 0 working days). */
export function hasTurnaround(days: number | null | undefined): boolean {
  return typeof days === 'number' && days > 0;
}

/** True when a fee is configured (> 0). */
export function hasFee(fee: number | null | undefined): boolean {
  return typeof fee === 'number' && fee > 0;
}

export function formatProcessingDays(days: number | null | undefined): string {
  if (!days || days <= 0) return 'Same day';
  return days === 1 ? '1 working day' : `${days} working days`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "04 Oct 2026". Accepts a date-only value or a server timestamp. */
export function formatDocDate(value?: string | null): string {
  if (!value) return '';
  const parsed = parseBackendDate(value);
  if (!parsed) return value;
  return `${String(parsed.getDate()).padStart(2, '0')} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
}

/** "04 Oct 2026, 1:45 PM". */
export function formatDocDateTime(value?: string | null): string {
  if (!value) return '';
  const parsed = parseBackendDate(value);
  if (!parsed) return value;
  const hour = parsed.getHours();
  const suffix = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${formatDocDate(value)}, ${displayHour}:${String(parsed.getMinutes()).padStart(2, '0')} ${suffix}`;
}

export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A file name safe on every platform: "Bonafide_Certificate_BC-2026-0004.pdf". */
export function safePdfFileName(documentName: string, documentNumber?: string | null): string {
  const base = [documentName, documentNumber]
    .filter(Boolean)
    .join(' ')
    .replace(/[^A-Za-z0-9\-_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80);
  return `${base || 'document'}.pdf`;
}

/** Keeps an attachment's own extension while stripping characters a file system may refuse. */
export function safeFileName(name: string, fallback = 'attachment'): string {
  const ext = fileExtension(name);
  const stem = (ext ? name.slice(0, -(ext.length + 1)) : name)
    .replace(/[^A-Za-z0-9\-_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80);
  return `${stem || fallback}${ext ? `.${ext.replace(/[^a-z0-9]/g, '')}` : ''}`;
}

// ── Errors ──────────────────────────────────────────────────────────────────

export interface DocumentError {
  /** The module's stable code, e.g. DOC.INVALID_FORM or DOC.STALE_FORM. */
  code?: string;
  message: string;
  fieldErrors: Record<string, string>;
}

interface DocumentErrorBody {
  code?: string;
  message?: string;
  fieldErrors?: Record<string, string> | null;
  title?: string;
  detail?: string;
  errors?: Record<string, string[] | string>;
}

/** Reads the module's { code, message, fieldErrors } refusal from an axios error. */
export function extractDocumentError(error: unknown, fallback: string): DocumentError {
  const response = (error as AxiosError<DocumentErrorBody | string>)?.response;
  if (!response) {
    return {
      message: 'Could not reach the school. Check your connection and try again.',
      fieldErrors: {},
    };
  }

  const data = response.data;
  if (typeof data === 'string') {
    return { message: data.trim() || fallback, fieldErrors: {} };
  }
  if (data && typeof data === 'object') {
    const fieldErrors: Record<string, string> = {};
    if (data.fieldErrors && typeof data.fieldErrors === 'object') {
      Object.entries(data.fieldErrors).forEach(([key, value]) => {
        if (value) fieldErrors[key] = String(value);
      });
    }
    let message = (data.message ?? data.detail ?? data.title ?? '').trim();
    if (!message && data.errors) {
      message = Object.values(data.errors)
        .flatMap(value => (Array.isArray(value) ? value : [value]))
        .filter(Boolean)
        .join('\n');
    }
    return { code: data.code || undefined, message: message || fallback, fieldErrors };
  }
  return { message: fallback, fieldErrors: {} };
}

/**
 * Server field errors keyed onto the form's own field keys (matched case-insensitively),
 * with the "Label: " prefix dropped since the message sits right under that label.
 */
export function mapServerFieldErrors(
  fieldErrors: Record<string, string>,
  fields: Pick<FormField, 'key' | 'label'>[],
): Record<string, string> {
  const mapped: Record<string, string> = {};
  Object.entries(fieldErrors).forEach(([key, message]) => {
    const field = fields.find(f => f.key.toLowerCase() === key.toLowerCase());
    if (!field) return;
    let text = message.trim();
    const prefix = `${field.label}:`;
    if (text.toLowerCase().startsWith(prefix.toLowerCase())) text = text.slice(prefix.length).trim();
    mapped[field.key] = text ? text.charAt(0).toUpperCase() + text.slice(1) : message;
  });
  return mapped;
}

export const STALE_FORM_CODE = 'DOC.STALE_FORM';
export const INVALID_FORM_CODE = 'DOC.INVALID_FORM';
